import {
  Button,
  CardContent,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import Card from "@material-ui/core/Card";
import { InfoOutlined } from "@material-ui/icons";
import CardTitle from "@saleor/components/CardTitle";
import PromptDialog from "@saleor/components/PromptDialog/PromptDialog";
import { couponUnlockCodesQuery } from "@saleor/coupons/queries";
import { couponUnlockCodes } from "@saleor/coupons/types/couponUnlockCodes";
import { couponSetCodesUrl } from "@saleor/coupons/url";
import { FormChange } from "@saleor/hooks/useForm";
import useNavigator from "@saleor/hooks/useNavigator";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useQuery } from "react-apollo";

import { TooltipArrowLeft } from "../CouponDetails/Tooltip";
import EnableUnlockCodesCard from "../EnableUnlockCodesCard/EnableUnlockCodesCard";
import UnlockCodesTable, {
  IUnlockCodeRow
} from "../UnlockCodesTable/UnlockCodesTable";
import { INITIAL_ROW_NUMBER, PROMPT_DIALOG_TEXTS } from "./constants";
import useExportAllApi from "./hooks/useExportAllApi";
import { useStyles } from "./styles";
import ExportAllBackdrop from "./subcomponents/ExportAllBackdrop/ExportAllBackdrop";
import usePaginationState from "./usePaginationState";

interface IUnlockCodesCardProps {
  isEnabledUnlockCodes: boolean;
  couponId: string;
  couponName: string;
  onChange: FormChange;
  isCouponEditable: boolean;
}

const UnlockCodesCard: FC<IUnlockCodesCardProps> = ({
  isEnabledUnlockCodes,
  couponId,
  couponName,
  onChange,
  isCouponEditable
}) => {
  const classes = useStyles({ isCouponEditable });
  const navigate = useNavigator();

  const [isRedirectWarningShown, setIsRedirectWarningShown] = useState(false);

  const baseCouponUnlockQueryVariables = {
    couponId,
    sortBy: {
      direction: "DESC",
      field: "UPDATED_AT"
    }
  };

  const {
    loading: isCouponUnlockCodesQueryLoading,
    data: unlockCodesData,
    refetch: refetchCouponUnlockCodes
  } = useQuery<couponUnlockCodes>(couponUnlockCodesQuery, {
    fetchPolicy: "network-only",
    variables: {
      ...baseCouponUnlockQueryVariables,
      first: INITIAL_ROW_NUMBER
    }
  });

  const {
    onRowNumberChange,
    hasNextPage,
    hasPreviousPage,
    onNextPage,
    onPrevPage,
    rowNumber,
    handleRefetchWithPaginatedState
  } = usePaginationState(
    unlockCodesData?.couponUnlockCodes?.pageInfo ?? undefined,
    INITIAL_ROW_NUMBER,
    refetchCouponUnlockCodes,
    baseCouponUnlockQueryVariables
  );

  const shapedUnlockCodesData: IUnlockCodeRow[] = useMemo(() => {
    if (isCouponUnlockCodesQueryLoading) {
      return [];
    }
    return unlockCodesData.couponUnlockCodes.edges.map(unlockCode => ({
      id: unlockCode.node.id,
      unlockCode: unlockCode.node.code,
      usageLimit: unlockCode.node.usageLimit,
      description: unlockCode.node.description
    }));
  }, [unlockCodesData, isCouponUnlockCodesQueryLoading]);

  const { exportAll, isLoading: isExportAllLoading } = useExportAllApi();

  const onCloseRedirectWarnDialog = useCallback(() => {
    setIsRedirectWarningShown(false);
  }, []);

  const onOpenRedirectWarnDialog = useCallback(() => {
    setIsRedirectWarningShown(true);
  }, []);

  const onRedirectToSetCodesPage = useCallback(() => {
    navigate(couponSetCodesUrl(couponId));
  }, [couponId, navigate]);

  const onToggleEnableUnlockCodes = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        target: {
          name: "isCodeUnlocked",
          value: event.target.checked
        }
      });
    },
    [onChange]
  );

  const isLoading = isCouponUnlockCodesQueryLoading || couponId === "";

  if (
    unlockCodesData?.couponUnlockCodes.totalCount === 0 ||
    isLoading ||
    !isEnabledUnlockCodes
  ) {
    return (
      <>
        <EnableUnlockCodesCard
          isEnabledUnlockCodes={isEnabledUnlockCodes}
          onChange={onChange}
          isCouponEditable={isCouponEditable && !isLoading}
          onSetCodesButtonClick={onOpenRedirectWarnDialog}
        />
        <PromptDialog
          isDialogShown={isRedirectWarningShown}
          onProceed={onRedirectToSetCodesPage}
          onCancel={onCloseRedirectWarnDialog}
          dialogTexts={PROMPT_DIALOG_TEXTS}
        />
      </>
    );
  }

  return (
    <>
      <Card className={classes.cardContainer}>
        <CardTitle
          className={classes.cardTitle}
          title={
            <div className={classes.radioContainer}>
              <FormControlLabel
                control={
                  <Switch
                    id="enableUnlockCodeSwitch"
                    color="primary"
                    checked={isEnabledUnlockCodes}
                    onChange={onToggleEnableUnlockCodes}
                  />
                }
                label="Enable Unlock Codes"
                labelPlacement="end"
                disabled={!isCouponEditable}
                className={classes.styledFormControlLabel}
              />
              <TooltipArrowLeft
                title="Toggle this if coupon requires an Unlock code."
                placement="right"
              >
                <InfoOutlined color="primary" />
              </TooltipArrowLeft>
            </div>
          }
          toolbar={
            <>
              <Button
                className={classes.exportAllButton}
                variant="outlined"
                onClick={exportAll(couponId, couponName)}
              >
                EXPORT ALL
              </Button>
              <Button
                className={classes.setCodesButton}
                variant="outlined"
                onClick={onOpenRedirectWarnDialog}
                disabled={!isCouponEditable}
              >
                SET CODES
              </Button>
            </>
          }
        />
        <CardContent className={classes.cardContentContainer}>
          <UnlockCodesTable
            couponId={couponId}
            unlockCodeData={shapedUnlockCodesData}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPreviousPage}
            onClickNextPage={onNextPage}
            onClickPrevPage={onPrevPage}
            onRowNumberChange={onRowNumberChange}
            rowNumber={rowNumber}
            isCouponEditable={isCouponEditable}
            handleRefetch={handleRefetchWithPaginatedState}
          />
        </CardContent>
      </Card>
      <PromptDialog
        isDialogShown={isRedirectWarningShown}
        onProceed={onRedirectToSetCodesPage}
        onCancel={onCloseRedirectWarnDialog}
        dialogTexts={PROMPT_DIALOG_TEXTS}
      />
      <ExportAllBackdrop isOpen={isExportAllLoading} />
    </>
  );
};

export default UnlockCodesCard;
